import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, PenTool } from 'lucide-react';
import { Note } from '../../types/notes';
import { Highlighter } from '../../lib/paper';
import { TagPill, Underline } from '../paper';
import { metaParts } from './noteCopy';

/** How the page was written, on one line with a pen — click to read it all. */
const WritingStyle: React.FC<{ text: string }> = ({ text }) => {
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setOpen((o) => !o)}
      aria-label={`Handwriting: ${text}`}
      className="mt-1 flex w-full cursor-pointer items-start gap-1.5 border-none bg-transparent p-0 text-left text-on-surface-variant"
    >
      <PenTool size={11} className="mt-[5px] shrink-0" />
      <span className={`font-headline text-[0.9rem] italic leading-6 ${open ? '' : 'line-clamp-1'}`}>{text}</span>
    </button>
  );
};

/** Title block: PB number, italic title with a drawn-on marker stroke, meta line, tags. */
export const NoteMasthead: React.FC<{ note: Note; tags: string[]; tone: Highlighter; linkTags?: boolean }> = ({
  note,
  tags,
  tone,
  linkTags = true,
}) => (
  <header>
    <div className="flex items-center gap-2">
      <span className="pb-eyebrow">PB-{note.id}</span>
      {note.public && (
        <span className="inline-flex items-center gap-1 rounded-full bg-hl-mint px-2 py-0.5 font-body text-[10px] font-semibold leading-[14px] text-hl-mint-ink">
          <Globe size={10} /> Public
        </span>
      )}
    </div>
    <h1 className="pb-heading-2 m-0 mt-1.5 md:text-[2.5rem] md:leading-[1.1]">{note.title ?? note.filename}</h1>
    <Underline width={96} color={tone.ink} seed={note.id} className="mt-1" />
    <p className="m-0 mt-2 truncate font-body text-[0.8125rem] text-on-surface-variant">
      {metaParts(note).join('  ·  ')}
    </p>
    {note.writing_style && <WritingStyle text={note.writing_style} />}
    {tags.length > 0 && (
      <div className="mt-3.5 flex flex-wrap gap-2">
        {tags.map((t) =>
          linkTags ? (
            <Link
              key={t}
              to={`/library?tag=${encodeURIComponent(t)}`}
              aria-label={`Show notes tagged ${t}`}
              className="transition-transform duration-150 active:scale-[0.92]"
            >
              <TagPill label={t} />
            </Link>
          ) : (
            <TagPill key={t} label={t} />
          ),
        )}
      </div>
    )}
  </header>
);
